import { useEffect, useState } from '@frontend/react';
import {
  Button,
  Icon,
  LoadingSpinnerPanel,
  PageContainer,
  Text,
  Title,
  Wordmark,
  XStack,
  YStack,
} from '@frontend/web-react';
import { PreviewTaskDetailChatTab } from '@frontend/web/components/DomainSpecific/Tasks/TaskDetailTab/PreviewTaskDetailChatTab';
import { PricingTerminologyProvider } from '@frontend/web/context';
import { useAnalytics, usePreviewTaskQuery } from '@frontend/web/hooks';
import { createFileRoute } from '@frontend/web/utils';
import { useNavigate } from '@tanstack/react-router';

const REFERRALS_URL = 'https://getduckbill.com/referrals';

const PreviewTask = () => {
  const { id: encodedId } = Route.useParams();
  const { data: previewData, error, isLoading } = usePreviewTaskQuery(encodedId);
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);

  const hasError = !!error;
  const hasQueryParams = !!window.location.search;

  useEffect(() => {
    if (isLoading) return;

    if (hasError) {
      track({ data: { encodedId }, event: 'viewFailed', topic: 'previewTask' });
    } else if (previewData) {
      track({ data: { hasQueryParams, taskId: previewData.id }, event: 'viewed', topic: 'previewTask' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, hasError, previewData?.id]);

  const getReferralsUrl = () => {
    const queryString = window.location.search;
    return queryString ? `${REFERRALS_URL}${queryString}` : REFERRALS_URL;
  };

  const handleSignUp = () => {
    track({ event: 'signupClicked', topic: 'previewTask' });
    window.open(getReferralsUrl(), '_blank');
  };

  const handleLogin = () => {
    track({ event: 'loginClicked', topic: 'previewTask' });
    navigate({ to: '/login' });
  };

  return (
    <PageContainer className="bg-trueWhite" isScrollable={false} padding="none">
      {/* Header */}
      <XStack
        alignItems="center"
        className="sticky top-none z-10 bg-trueWhite border-b border-neutral-200"
        justifyContent="between"
        px="lg"
        py="md"
      >
        <XStack alignItems="center" gap="sm">
          <Icon name="DuckbillLogo" size="lg" />
          <Wordmark className="fill-neutral-950" height={18} width={72} />
        </XStack>
        <XStack alignItems="center" gap="md">
          <Button onClick={handleLogin} variant="tertiary">
            Log in
          </Button>
          <Button onClick={handleSignUp} variant="primary">
            Sign Up
          </Button>
        </XStack>
      </XStack>

      {hasError ? (
        /* Error State */
        <YStack alignItems="center" className="flex-1 justify-center px-lg py-xxl bg-cream-100">
          <YStack
            alignItems="center"
            className="bg-trueWhite border border-neutral-200 rounded-lg shadow-one max-w-md w-full"
            gap="xl"
            p="xl"
          >
            <YStack alignItems="center" gap="sm">
              <Icon name="DuckbillLogo" size="xl" />
              <Title className="text-center" type="h4">
                This task has flown the coop
              </Title>
              <Text className="text-center" color="secondary" type="p-md">
                This preview is no longer available, but there&apos;s so much more Duckbill can handle for you.
              </Text>
            </YStack>
            <YStack alignItems="center" className="w-full" gap="md">
              <Button fullWidth onClick={handleSignUp} variant="primary">
                Get Started with Duckbill
              </Button>
              <Text color="secondary" type="p-sm">
                Already have an account?{' '}
                <Button className="inline p-none" onClick={handleLogin} variant="tertiary">
                  Log in
                </Button>
              </Text>
            </YStack>
          </YStack>
        </YStack>
      ) : (
        <>
          {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
          <YStack className="flex-1 min-h-0 overflow-hidden md:flex-row">
            {/* Left Panel — Hero + CTA */}
            <YStack className="py-sm px-lg md:p-xl md:w-1/3 border-b md:border-b-0 md:border-r border-neutral-200 bg-cream-100 md:overflow-y-auto md:justify-between">
              {/* Top group — title + description */}
              <YStack gap="md">
                {/* Title row — always visible, tappable on mobile to expand/collapse */}
                <XStack
                  alignItems="center"
                  className="cursor-pointer md:cursor-default"
                  justifyContent="between"
                  onClick={() => setIsPanelExpanded((prev: boolean) => !prev)}
                >
                  <Title type="h3">{previewData?.name ?? '...'}</Title>
                  <Icon
                    className={`md:hidden transition-transform ${isPanelExpanded ? 'rotate-180' : ''}`}
                    name="IconChevronDown"
                    size="md"
                  />
                </XStack>

                {/* Description — hidden on mobile until expanded, always visible on md+ */}
                <YStack className={`${isPanelExpanded ? 'flex' : 'hidden'} md:flex`} gap="md">
                  <Text color="secondary" type="p-lg">
                    Duckbill Did This Task For {previewData?.memberFirstName ?? '...'}
                  </Text>
                  <Text color="secondary" type="p-md">
                    Duckbill is your AI-powered personal assistant backed by real humans. We handle life&apos;s busywork
                    — from phone calls to research to scheduling — so you don&apos;t have to.
                  </Text>
                </YStack>
              </YStack>

              {/* CTA — hidden on mobile until expanded, anchored to bottom on md+ via justify-between */}
              <YStack alignItems="center" className={`${isPanelExpanded ? 'flex' : 'hidden'} md:flex pt-lg`} gap="sm">
                <Button fullWidth onClick={handleSignUp} variant="primary">
                  Get Started with Duckbill
                </Button>
                <Text className="text-center" color="secondary" type="p-sm">
                  Already have an account?{' '}
                  <Button className="inline p-none" onClick={handleLogin} variant="tertiary">
                    Log in
                  </Button>
                </Text>
              </YStack>
            </YStack>

            {/* Right Panel — Message Thread */}
            {/* eslint-disable-next-line tailwindcss/no-custom-classname */}
            <YStack className="flex-1 min-h-0 overflow-hidden">
              {isLoading || !previewData ? (
                <LoadingSpinnerPanel fullScreen />
              ) : (
                <PreviewTaskDetailChatTab
                  isLoading={isLoading}
                  messages={previewData.messages}
                  onSignUp={handleSignUp}
                  taskId={previewData.id}
                />
              )}
            </YStack>
          </YStack>
        </>
      )}
    </PageContainer>
  );
};

const PreviewTaskWithProviders = () => (
  <PricingTerminologyProvider>
    <PreviewTask />
  </PricingTerminologyProvider>
);

export const Route = createFileRoute('/preview/$id')({
  component: PreviewTaskWithProviders,
});
